import { useEffect, useRef } from "react";

/**
 * Lightweight canvas fireworks — small particle bursts triggered every ~700ms
 * across the screen. No deps. Cleans up on unmount.
 */
export function Fireworks() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const dpr = Math.min(window.devicePixelRatio, 2);
    let w = 0,
      h = 0;
    const resize = () => {
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    interface P {
      x: number;
      y: number;
      vx: number;
      vy: number;
      life: number;
      max: number;
      hue: number;
    }
    const parts: P[] = [];
    const burst = (x: number, y: number, hue: number) => {
      const n = 24 + Math.floor(Math.random() * 16);
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + Math.random() * 0.2;
        const speed = 2.5 + Math.random() * 2.2;
        parts.push({
          x,
          y,
          vx: Math.cos(a) * speed,
          vy: Math.sin(a) * speed,
          life: 0,
          max: 60 + Math.random() * 30,
          hue,
        });
      }
    };

    const colors = [10, 45, 200, 280, 320, 110, 0];
    let lastBurst = 0;
    let raf = 0;
    const tick = (t: number) => {
      raf = requestAnimationFrame(tick);
      ctx.clearRect(0, 0, w, h);
      if (t - lastBurst > 600) {
        const x = Math.random() * w * 0.8 + w * 0.1;
        const y = Math.random() * h * 0.45 + h * 0.1;
        const hue = colors[Math.floor(Math.random() * colors.length)]!;
        burst(x, y, hue);
        lastBurst = t;
      }
      for (let i = parts.length - 1; i >= 0; i--) {
        const p = parts[i]!;
        p.life++;
        p.vy += 0.06; // gravity
        p.x += p.vx;
        p.y += p.vy;
        const alpha = 1 - p.life / p.max;
        if (alpha <= 0) {
          parts.splice(i, 1);
          continue;
        }
        ctx.fillStyle = `hsla(${p.hue}, 90%, 65%, ${alpha})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 2.4, 0, Math.PI * 2);
        ctx.fill();
      }
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={ref} className="fireworks-canvas" />;
}
